import { useMemo, useState } from 'react';
import { Pencil, RotateCcw } from 'lucide-react';

import { agentRanges, buildQueue } from '../../lib/agentSpans.js';
import { buildFinalCaptionWithRanges } from '../../lib/derive.js';
import { humanAddedSpans } from '../../lib/remap.js';
import { renderSegments } from '../../lib/renderSegments.jsx';
import { TAG_META } from '../../lib/spans.js';
import { CaptionSurface } from './CaptionSurface.jsx';
import { TagLegend } from './SpanRail.jsx';
import { DecisionLegend } from './admin/DecisionQueue.jsx';

/**
 * The final caption, rendered with colour rather than as plain text.
 *
 * Three layers, matching the old tool:
 *   - substituted text keeps the colour of the tag that replaced it;
 *   - kept-with-note text and its `<note>` show in the uncertain colour;
 *   - anything the human typed that the agent did not write is purple.
 *
 * For admins a fourth layer is added: the agent's own spans and verified missing
 * details, coloured by the verdict given to each. They are located against THIS text
 * rather than reusing the working-caption offsets, because the two strings differ once
 * the operations apply - a replacement changes length, a removal deletes a span.
 *
 * Read-only by default, because the text is derived. Editing it is a deliberate
 * escape hatch - and it has to exist, because the final caption is hand-editable after
 * the operations apply (true for 9 of the 20 legacy annotations).
 */
export function FinalPreview({ draft, onChange, onResync, agentLayer = null }) {
  const [editing, setEditing] = useState(false);

  const derived = useMemo(
    () => buildFinalCaptionWithRanges(draft.working, draft.spans),
    [draft.working, draft.spans],
  );

  const text = draft.finalCaption ?? derived.text;
  const diverged = text !== derived.text;

  // Highlight the derived ranges only while the text still matches the derivation;
  // once it is hand-edited the offsets no longer describe it.
  const ranges = useMemo(() => {
    const layers = [];

    if (!diverged) {
      for (const range of derived.ranges) {
        const meta = TAG_META[range.label] || {};
        layers.push({
          start: range.start_char,
          end: range.end_char,
          className: meta.className,
          title:
            range.kind === 'replacement'
              ? `Replaced “${range.original}”`
              : `${meta.label}: ${range.original}`,
        });
      }
    }

    for (const added of humanAddedSpans(draft.agentCaption, text, diverged ? [] : derived.ranges)) {
      layers.push({
        start: added.start_char,
        end: added.end_char,
        className: 'human-added-text-highlight',
        title: 'Text you added',
      });
    }

    // Appended LAST on purpose. renderSegments takes the first passive range's classes
    // when several overlap, so the human's own tags keep the segment and the agent layer
    // only colours what the human has not already claimed.
    if (agentLayer) {
      const queue = buildQueue(agentLayer.finalSpans, agentLayer.missingDetails, text);
      layers.push(
        ...agentRanges(queue, agentLayer.decisions, null, agentLayer.detailDecisions),
      );
    }

    return layers;
  }, [derived, text, diverged, draft.agentCaption, agentLayer]);

  const content = useMemo(() => renderSegments(text, ranges), [text, ranges]);

  return (
    <div className="final-preview-block">
      <div className="row-between" style={{ marginBottom: 'var(--space-2)' }}>
        <span className="section-title" style={{ margin: 0 }}>
          Final caption
        </span>
        <div className="row gap-1">
          {diverged && (
            <button
              type="button"
              className="btn sm ghost"
              title="Discard your manual edits and rebuild from the tags"
              onClick={() => onResync(derived.text)}
            >
              <RotateCcw size={12} /> Rebuild from tags
            </button>
          )}
          <button type="button" className="btn sm ghost" onClick={() => setEditing((v) => !v)}>
            <Pencil size={12} /> {editing ? 'Done' : 'Edit'}
          </button>
        </div>
      </div>

      {editing ? (
        <textarea
          className="textarea caption-body"
          value={text}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Final caption"
          rows={5}
        />
      ) : (
        <p className="caption-surface caption-body final-preview">{content}</p>
      )}

      <div className="row-between" style={{ marginTop: 'var(--space-2)' }}>
        <TagLegend />
        {agentLayer && <DecisionLegend />}
        {diverged && <span className="tiny faint">edited by hand</span>}
      </div>
    </div>
  );
}
